import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Mail,
  Phone,
  Building2,
  Calendar,
  Trash2,
  RefreshCw,
  MessageSquare,
  Euro,
  Briefcase,
  LogOut,
  Shield,
  Settings,
  User,
  Lock,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700'
};

const statusLabels = {
  new: 'Neu',
  contacted: 'Kontaktiert',
  completed: 'Abgeschlossen'
};

const Admin = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('contacts');
  
  // Settings state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem('adminLoggedIn');
    if (isLoggedIn !== 'true') {
      navigate('/admin');
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminLoggedIn');
    sessionStorage.removeItem('adminUsername');
    toast.success('Erfolgreich abgemeldet');
    navigate('/admin');
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${API}/contact${params}`);
      setContacts(response.data.contacts);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Fehler beim Laden der Anfragen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem('adminLoggedIn');
    if (isLoggedIn === 'true') {
      fetchContacts();
    }
  }, [filterStatus]);

  const updateStatus = async (contactId, newStatus) => {
    try {
      await axios.patch(`${API}/contact/${contactId}`, { status: newStatus });
      toast.success('Status aktualisiert');
      fetchContacts();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Möchten Sie diese Anfrage wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/contact/${contactId}`);
      toast.success('Anfrage gelöscht');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleUpdateCredentials = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      toast.error('Bitte geben Sie Ihr aktuelles Passwort ein');
      return;
    }
    
    if (newPassword && newPassword !== confirmPassword) {
      toast.error('Die neuen Passwörter stimmen nicht überein');
      return;
    }
    
    if (newPassword && newPassword.length < 6) {
      toast.error('Das neue Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    setSavingSettings(true);
    try {
      const response = await axios.post(`${API}/auth/update`, {
        current_password: currentPassword,
        new_username: newUsername || null,
        new_password: newPassword || null
      });
      
      if (response.data.success) {
        toast.success('Zugangsdaten erfolgreich aktualisiert');
        
        // Update session storage with new username if changed
        if (newUsername) {
          sessionStorage.setItem('adminUsername', newUsername);
        }
        
        // Clear form
        setCurrentPassword('');
        setNewUsername('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      console.error('Error updating credentials:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren der Zugangsdaten');
    } finally {
      setSavingSettings(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-slate-900 pt-8 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <Shield className="text-white" size={20} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-slate-400 text-sm">Verwaltung & Einstellungen</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-slate-400 text-sm hidden sm:block">
                Angemeldet als: <span className="text-white font-medium">{sessionStorage.getItem('adminUsername')}</span>
              </span>
              <Button 
                onClick={handleLogout} 
                variant="outline" 
                className="border-slate-600 text-white hover:bg-slate-800"
              >
                <LogOut size={16} className="mr-2" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 w-full max-w-md mb-6 bg-white shadow-sm">
            <TabsTrigger value="contacts" className="gap-2">
              <MessageSquare size={16} />
              Anfragen
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings size={16} />
              Einstellungen
            </TabsTrigger>
          </TabsList>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            {/* Stats & Filter Bar */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-sm text-slate-500">Gesamt Anfragen</p>
                      <p className="text-2xl font-bold text-slate-900">{total}</p>
                    </div>
                    <div className="h-12 w-px bg-slate-200" />
                    <div>
                      <p className="text-sm text-slate-500">Filter</p>
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle</SelectItem>
                          <SelectItem value="new">Neu</SelectItem>
                          <SelectItem value="contacted">Kontaktiert</SelectItem>
                          <SelectItem value="completed">Abgeschlossen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={fetchContacts} variant="outline" className="gap-2">
                    <RefreshCw size={16} />
                    Aktualisieren
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Contacts List */}
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto text-blue-600 mb-4" size={32} />
                <p className="text-slate-500">Lade Anfragen...</p>
              </div>
            ) : contacts.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <MessageSquare className="mx-auto text-slate-300 mb-4" size={48} />
                  <p className="text-slate-500">Keine Anfragen gefunden</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4 pb-12">
                {contacts.map((contact) => (
                  <Card key={contact.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                        <div>
                          <CardTitle className="text-lg">{contact.name}</CardTitle>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDate(contact.created_at)}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[contact.status]}`}>
                              {statusLabels[contact.status]}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select
                            value={contact.status}
                            onValueChange={(value) => updateStatus(contact.id, value)}
                          >
                            <SelectTrigger className="w-36 h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">Neu</SelectItem>
                              <SelectItem value="contacted">Kontaktiert</SelectItem>
                              <SelectItem value="completed">Abgeschlossen</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteContact(contact.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail size={14} className="text-slate-400" />
                          <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                            {contact.email}
                          </a>
                        </div>
                        {contact.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone size={14} className="text-slate-400" />
                            <a href={`tel:${contact.phone}`} className="hover:text-blue-600">
                              {contact.phone}
                            </a>
                          </div>
                        )}
                        {contact.company && (
                          <div className="flex items-center gap-2 text-sm">
                            <Building2 size={14} className="text-slate-400" />
                            <span>{contact.company}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <Briefcase size={14} className="text-slate-400" />
                          <span>{contact.service}</span>
                        </div>
                        {contact.budget && (
                          <div className="flex items-center gap-2 text-sm">
                            <Euro size={14} className="text-slate-400" />
                            <span>{contact.budget}</span>
                          </div>
                        )}
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{contact.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="max-w-xl">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock size={20} />
                    Zugangsdaten ändern
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleUpdateCredentials} className="space-y-6">
                    {/* Current Password */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Aktuelles Passwort *
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <Input
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="Aktuelles Passwort eingeben"
                          required
                          className="pl-10 pr-10 bg-slate-50"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <hr className="border-slate-200" />

                    {/* New Username */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Neuer Benutzername
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <Input
                          type="text"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          placeholder={`Aktuell: ${sessionStorage.getItem('adminUsername')}`}
                          className="pl-10 bg-slate-50"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Leer lassen, um den aktuellen Benutzernamen zu behalten</p>
                    </div>

                    {/* New Password */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Neues Passwort
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Neues Passwort eingeben"
                          className="pl-10 pr-10 bg-slate-50"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Neues Passwort bestätigen
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Neues Passwort wiederholen"
                          className="pl-10 bg-slate-50"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Leer lassen, um das aktuelle Passwort zu behalten</p>
                    </div>

                    <Button
                      type="submit"
                      disabled={savingSettings}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {savingSettings ? (
                        <span className="flex items-center gap-2">
                          <RefreshCw className="animate-spin" size={16} />
                          Wird gespeichert...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save size={16} />
                          Änderungen speichern
                        </span>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
